import { Component, inject } from '@angular/core';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { OfflineFormsClient } from './offline-forms.client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lfw-index-db-size-dialog',
  template: `
    <h4 mat-dialog-title>{{ 'INDEXDB.MEMORYUSAGE' | translate }}</h4>
    <mat-dialog-content>
      @if (storageFacts$ | async; as storageFacts) {
        <div class="caption">{{ 'INDEXDB.ORDERCOUNT' | translate }}</div>
        <div class="info">{{ storageFacts.itemCount }}</div>
        <div class="caption">{{ 'INDEXDB.USEDMEMORY' | translate }}</div>
        <div class="info">{{ storageFacts.usedSpace }}</div>
        <div class="caption">{{ 'INDEXDB.AVAILMEMORY' | translate }}</div>
        <div class="info">{{ storageFacts.availableSpace }}</div>
      }
    </mat-dialog-content>
    <mat-dialog-actions>
      <button mat-button mat-dialog-close cdkFocusInitial>Ok</button>
    </mat-dialog-actions>
  `,
  styles: `
    mat-dialog-content {
      display: grid;
      grid-template-columns: 70% 30%;
    }
  `,
  imports: [NgxIndexedDBModule, MatDialogModule, AsyncPipe, MatButtonModule, TranslatePipe]
})
export class IndexDbSizeDialogComponent {
  storageFacts$: Observable<StorageFacts>;
  readonly dialogRef = inject(MatDialogRef<IndexDbSizeDialogComponent>);
  private offlineOrdersClient = inject(OfflineFormsClient);
  private translate = inject(TranslateService);

  constructor() {
    this.storageFacts$ = this.offlineOrdersClient.offlineOrders$.pipe(
      takeUntilDestroyed(),
      switchMap(async () => {
        const estimate = await navigator.storage.estimate();
        // calculate remaining storage in MB
        return {
          availableSpace: this.humanReadable((estimate.quota || 0) - (estimate.usage || 0)),
          usedSpace: this.humanReadable(estimate.usage || 0),
          itemCount: await this.offlineOrdersClient.count()
        };
      })
    );
  }

  private humanReadable(size: number): string {
    const i = size == 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
    return +(size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
  }
}

interface StorageFacts {
  availableSpace: string;
  itemCount: number;
  usedSpace: string;
}
